import { ChangeEvent, FC, useEffect, useState } from "react";
import { Col, Form, Modal, Row } from "react-bootstrap";
import { Lock } from "lucide-react";
import { registerUser, getTeamMembers } from "../../../api/common";
import SubmitButton from "../../../Components/Button";
import LoadingOverlay from "../../../Components/LoadingOverlay";
import TextInput from "../../../Components/TextInput";
import SelectBox from "../../../Components/SelectBox";
import Alert from "../../../Components/AlertPopup";
import { ERROR_MESSAGE, SUCCESS_MESSAGE } from "../../../constants";
import styles from "./style.module.scss";

const AUDIT_ROLE_OPTIONS = [
  { value: "", label: "--Select--" },
  { value: "Lead Auditor", label: "Lead Auditor" },
  { value: "Auditor", label: "Auditor" },
];

interface UserData {
  userId?: string;
  username?: string;
  name?: string;
  email?: string;
  roleType?: string;
}

interface Props {
  show: boolean;
  onClose: () => void;
  mode: "add" | "edit";
  userData?: UserData | null;
}

const AddEditUserModal: FC<Props> = ({
  show,
  onClose,
  mode,
  userData = null,
}) => {
  const [userName, setUserName] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("");
  const [loading, setLoading] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [teamMembers, setTeamMembers] = useState<any[]>([]);

  useEffect(() => {
    if (show) {
      if (mode === "edit" && userData) {
        setUserName(userData.username || userData.name || "");
        setDisplayName(userData.name || "");
        setEmail(userData.email || "");
        setRole(userData.roleType || "");
      } else {
        resetForm();
        fetchTeamMembers();
      }
    }
  }, [show, mode, userData]);

  const fetchTeamMembers = async () => {
    try {
      const response = await getTeamMembers();
      if (response && response.length > 0) {
        setTeamMembers(response);
      }
    } catch {
      // silently handle
    }
  };

  const resetForm = () => {
    setUserName("");
    setDisplayName("");
    setEmail("");
    setRole("");
  };

  const isEdit = mode === "edit";

  const isFormValid = isEdit
    ? role.trim() !== ""
    : userName.trim() !== "" &&
    displayName.trim() !== "" &&
    email.trim() !== "" &&
    role.trim() !== "";

  const userOptions = teamMembers.map((member: any) => ({
    value: member.name || member.username || "",
    label: member.name || member.username || "",
  }));

  const handleUserSelect = (e: ChangeEvent<HTMLSelectElement>) => {
    const selectedName = e.currentTarget.value;
    setUserName(selectedName);
    const selected = teamMembers.find(
      (m: any) => (m.name || m.username) === selectedName
    );
    if (selected) {
      setDisplayName(selected.name || "");
      setEmail(selected.email || "");
    }
  };

  const handleSubmit = async (event: ChangeEvent<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();

    if (!isFormValid) return;

    const formData = {
      roleType: role,
      name: displayName,
      email,
      username: userName,
    };
    setLoading(true);
    try {
      await registerUser(formData);
      setShowToast(true);
      resetForm();
      setLoading(false);
    } catch {
      setLoading(false);
      setErrorMessage(ERROR_MESSAGE.API_ERROR_MESSAGE);
      setShowErrorToast(true);
    }
  };

  const handleToastClose = () => {
    setShowToast(false);
    onClose();
  };

  const title = isEdit ? "Update User" : "Add User";

  return (
    <Modal show={show} onHide={onClose} centered className={styles.modaluser}>
      <LoadingOverlay loading={loading} />
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title className={styles.modalTitle}>{title}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Alert
            show={showToast}
            onClose={handleToastClose}
            message={
              isEdit
                ? "User has been updated successfully!"
                : SUCCESS_MESSAGE.USER_CREATED_SUCCESS
            }
          />
          <Alert
            type="error"
            show={showErrorToast}
            onClose={() => setShowErrorToast(false)}
            message={errorMessage}
          />

          {/* User Name */}
          <Row className={styles.fieldRow}>
            <Col xs={12}>
              <Form.Label className={styles.fieldLabel}>
                User Name<span className={styles.mandatory}>*</span>
              </Form.Label>
            </Col>
            <Col xs={12}>
              {isEdit ? (
                <div className={styles.lockedField}>
                  <Form.Control
                    type="text"
                    value={userName}
                    readOnly
                    className={styles.lockedInput}
                  />
                  <Lock size={14} className={styles.lockIcon} />
                </div>
              ) : (
                <SelectBox
                  options={userOptions}
                  name="userName"
                  id="userName"
                  onChange={handleUserSelect}
                  defaultValue={userName}
                />
              )}
            </Col>
          </Row>

          {/* Display Name */}
          <Row className={styles.fieldRow}>
            <Col xs={12}>
              <Form.Label className={styles.fieldLabel}>
                Display Name<span className={styles.mandatory}>*</span>
              </Form.Label>
            </Col>
            <Col xs={12}>
              {isEdit ? (
                <div className={styles.lockedField}>
                  <Form.Control
                    type="text"
                    value={displayName}
                    readOnly
                    className={styles.lockedInput}
                  />
                  <Lock size={14} className={styles.lockIcon} />
                </div>
              ) : (
                <TextInput
                  name="displayName"
                  id="displayName"
                  placeholder="Enter display name"
                  value={displayName}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setDisplayName(e.currentTarget.value)
                  }
                />
              )}
            </Col>
          </Row>

          {/* Email ID */}
          <Row className={styles.fieldRow}>
            <Col xs={12}>
              <Form.Label className={styles.fieldLabel}>
                Email ID<span className={styles.mandatory}>*</span>
              </Form.Label>
            </Col>
            <Col xs={12}>
              {isEdit ? (
                <div className={styles.lockedField}>
                  <Form.Control
                    type="text"
                    value={email}
                    readOnly
                    className={styles.lockedInput}
                  />
                  <Lock size={14} className={styles.lockIcon} />
                </div>
              ) : (
                <TextInput
                  name="email"
                  id="email"
                  placeholder="Enter email address"
                  value={email}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setEmail(e.currentTarget.value)
                  }
                />
              )}
            </Col>
          </Row>

          {/* Role */}
          <Row className={styles.fieldRow}>
            <Col xs={12}>
              <Form.Label className={styles.fieldLabel}>
                Role<span className={styles.mandatory}>*</span>
              </Form.Label>
            </Col>
            <Col xs={12}>l
              <SelectBox
                options={AUDIT_ROLE_OPTIONS}
                name="role"
                id="role"
                onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                  setRole(e.currentTarget.value)
                }
                defaultValue={role}
              />
            </Col>
          </Row>
        </Modal.Body>

        <Modal.Footer>
          <SubmitButton type="secondary" onClick={onClose} className={styles.cancelBtn}>
            Cancel
          </SubmitButton>
          <SubmitButton disabled={!isFormValid} className={styles.saveBtn}>Save</SubmitButton>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default AddEditUserModal;
